import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { useAuth } from './hooks/useAuth';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import PackagesList from './pages/PackagesList';
import HolidayPackagesList from './pages/HolidayPackagesList';
import PackageDetail from './pages/PackageDetail';
import PackageForm from './components/PackageForm';
import DateSlotForm from './components/DateSlotForm';
import ChangePassword from './components/ChangePassword';
import ContactUs from './pages/ContactUs';
import AllBookings from './pages/AllBookings';
import Sidebar from './components/Sidebar';
import Toast from './components/Toast';
import AuthGuard from './components/AuthGuard';

// Protected Route Component with Layout
const ProtectedRoute = ({ children, requiredRole = null }) => {
  return (
    <AuthGuard requiredRole={requiredRole}>
      <div className="flex h-screen bg-gray-100">
        <div className="w-64 flex-shrink-0">
          <Sidebar />
        </div>
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </AuthGuard>
  );
};

// Main App Routes
const AppRoutes = () => {
  const { state, hideToast } = useApp();
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <Router basename="/vendor-app">
      <Toast 
        isOpen={state.toast.isOpen}
        onClose={hideToast}
        type={state.toast.type}
        message={state.toast.message}
      />
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            isLoading ? (
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading...</p>
                </div>
              </div>
            ) : isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login />
            )
          } 
        />
        
        <Route 
          path="/forgot-password" 
          element={
            isLoading ? (
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading...</p>
                </div>
              </div>
            ) : isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ForgotPassword />
            )
          } 
        />
        
        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/packages"
          element={
            <ProtectedRoute>
              <PackagesList />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/holiday-packages"
          element={
            <ProtectedRoute>
              <HolidayPackagesList />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/packages/new"
          element={
            <ProtectedRoute>
              <PackageForm />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/packages/:id"
          element={
            <ProtectedRoute>
              <PackageDetail />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/packages/:id/edit"
          element={
            <ProtectedRoute>
              <PackageForm isEdit />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/packages/:packageId/date-slots/new"
          element={
            <ProtectedRoute>
              <DateSlotForm />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/packages/:packageId/date-slots/:id/edit"
          element={
            <ProtectedRoute>
              <DateSlotForm isEdit />
            </ProtectedRoute>
          }
        />
        
        {/* Change Password Route */}
        <Route 
          path="/change-password" 
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        
        {/* All Bookings Route */}
        <Route 
          path="/all-bookings" 
          element={
            <ProtectedRoute>
              <AllBookings />
            </ProtectedRoute>
          }
        />
        
        {/* Contact Us Route */}
        <Route 
          path="/contact-us" 
          element={
            <ProtectedRoute>
              <ContactUs />
            </ProtectedRoute>
          }
        />
        
        {/* Default redirect */}
        <Route 
          path="/" 
          element={<Navigate to="/dashboard" replace />} 
        />
        
        
        {/* 404 Route */}
        <Route 
          path="*" 
          element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900">Page not found</h1>
                <p className="text-gray-600 mt-2">The page you're looking for doesn't exist.</p>
                <button
                  onClick={() => window.history.back()}
                  className="btn-primary mt-4"
                >
                  Go Back
                </button>
              </div>
            </div>
          } 
        />
      </Routes>
    </Router>
  );
};

// Main App Component
const App = () => {
  return (
    <AppProvider>
      <div className="App">
        <AppRoutes />
      </div>
    </AppProvider>
  );
};

export default App;
